import Phaser from 'phaser';

/**
 * Singleton utility class to manage shared resources between scenes
 * like textures, sounds, and other assets that should be created once
 * and reused across multiple scenes.
 */
export class SharedResources {
  private static instance: SharedResources;
  private scene: Phaser.Scene | null = null;
  private resources: Map<string, any> = new Map();
  private refCounts: Map<string, number> = new Map();
  
  // Asset keys
  private static readonly GRADIENT_TEXTURE_KEY = 'gradientTexture';
  public static readonly MITOCHONDRION_TEXTURE_KEY = 'mitochondrionTexture';
  public static readonly MITOCHONDRION_MIN_TEXTURE_KEY = 'mito_v3_min';
  public static readonly LYSOSOME_TEXTURE_KEY = 'lyso_v2_min';
  public static readonly GLUCOSE_TEXTURE_KEY = 'glucose_amber_v1';
  
  /**
   * Private constructor to enforce singleton pattern
   */
  private constructor() {}
  
  /**
   * Get the singleton instance
   */
  public static getInstance(): SharedResources {
    if (!SharedResources.instance) {
      SharedResources.instance = new SharedResources();
    }
    return SharedResources.instance;
  }
  
  /**
   * Initialize with a scene reference
   * @param scene A reference scene to use for creating resources
   */
  public initialize(scene: Phaser.Scene): void {
    this.scene = scene;
    console.log('SharedResources initialized with scene:', scene.scene.key);
  }
  
  /**
   * Check if the gradient texture exists
   */
  public hasGradientTexture(): boolean {
    return this.resources.has(SharedResources.GRADIENT_TEXTURE_KEY) || 
           (this.scene?.textures.exists(SharedResources.GRADIENT_TEXTURE_KEY) ?? false);
  }

  /**
   * TODO: we should make these methods generic for any organelle asset
   * Check if the mitochondrion texture exists
   */
  public hasMitochondrionTexture(): boolean {
    return this.resources.has(SharedResources.MITOCHONDRION_TEXTURE_KEY) || 
           (this.scene?.textures.exists(SharedResources.MITOCHONDRION_TEXTURE_KEY) ?? false);
  }

  /**
   * Ensure mitochondrion texture is loaded
   * @returns The key of the mitochondrion texture
   */
  public getMitochondrionTexture(): string {
    // If texture is not loaded yet, we need to ensure it's loaded by the scene
    if (!this.hasMitochondrionTexture() && this.scene) {
      if (!this.scene.textures.exists(SharedResources.MITOCHONDRION_TEXTURE_KEY)) {
        console.log('Mitochondrion texture not loaded, please ensure it is loaded in the preload function');
      }
    }
    
    // Increment the reference count
    this.incrementRefCount(SharedResources.MITOCHONDRION_TEXTURE_KEY);
    
    return SharedResources.MITOCHONDRION_TEXTURE_KEY;
  }

  /**
   * Preload the mitochondrion texture
   * @param scene The scene to use for loading
   */
  public preloadMitochondrionTexture(scene: Phaser.Scene): void {
    if (!scene.textures.exists(SharedResources.MITOCHONDRION_TEXTURE_KEY)) {
      console.log('Preloading mitochondrion texture from assets/images/mito_v3_min.png');
      // Load the image directly from the public path
      scene.load.image(SharedResources.MITOCHONDRION_TEXTURE_KEY, 'assets/images/mito_v3_min.png');
    }
  }
  
  /**
   * Create a fallback texture for the mitochondrion if the image fails to load
   * @param scene The scene to use for creating the texture
   */
  private createFallbackMitochondrionTexture(scene: Phaser.Scene): void {
    // This method is no longer used as we're focusing on loading the actual image
  }

  /**
   * Create a mitochondrion sprite
   * @param scene The scene to add the sprite to
   * @param x X position
   * @param y Y position
   * @returns The created sprite
   */
  public createMitochondrionSprite(scene: Phaser.Scene, x: number, y: number): Phaser.GameObjects.Sprite {
    // Ensure the texture exists
    const textureKey = this.getMitochondrionTexture();
    
    // Create the sprite
    const sprite = scene.add.sprite(x, y, textureKey);
    sprite.setOrigin(0.5);
    
    // Make sure transparency is supported
    sprite.setPipeline('MultiPipeline');
    sprite.setBlendMode(Phaser.BlendModes.NORMAL);
    
    console.log('Created mitochondrion sprite:', 
      `position(${sprite.x}, ${sprite.y})`
    );
    
    return sprite;
  }
  
  /**
   * Create the gradient background texture if it doesn't exist
   * @returns The key of the created texture
   */
  public createGradientTexture(): void {
    // Skip if texture already exists
    if (this.scene?.textures.exists(SharedResources.GRADIENT_TEXTURE_KEY)) return;

    // Check if we're on a mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
                    (this.scene?.game.device.os.android || this.scene?.game.device.os.iOS);
    
    // Determine appropriate texture size based on device
    // Mobile GPUs typically have max texture size of 4096, many limit to 2048
    const size = isMobile ? 2048 : 4096;
    
    console.log(`Creating gradient texture with size ${size}x${size} for ${isMobile ? 'mobile' : 'desktop'}`);

    // Colors to use for the gradient (from outer to inner) - restored blue palette
    const colorStops = [
      0x000011, // Almost black (outer edge)
      0x001133, // Very dark blue
      0x001a4d, // Deep blue
      0x002b80, // Navy blue
      0x0033cc, // Royal blue
      0x0044ff, // Bright blue
      0x3377ff, // Sky blue
      0x66aaff, // Light blue
      0x88bbff, // Pale blue
      0xaaddff, // Very light blue
      0xccf2ff, // Almost white blue
      0xeeffff  // Center highlight
    ];
    
    // Use fewer color stops for mobile for better performance - restored blue palette
    const mobileColorStops = [
      0x000011, // Almost black (outer edge)
      0x001a4d, // Deep blue
      0x0033cc, // Royal blue
      0x3377ff, // Sky blue
      0xaaddff, // Very light blue
      0xeeffff  // Center highlight
    ];
    
    // Use the appropriate color stops based on device
    const gradientColors = isMobile ? mobileColorStops : colorStops;

    // Create a graphics object
    const graphics = this.scene?.make.graphics({ x: 0, y: 0 });
    
    if (!graphics || !this.scene) {
      console.error('Failed to create graphics object for gradient texture');
      return;
    }

    // Draw concentric circles with different colors to create the gradient effect
    const sectionSize = size / 2 / gradientColors.length;

    // Draw from outer to inner
    for (let i = 0; i < gradientColors.length; i++) {
      const radius = size / 2 - i * sectionSize;
      graphics.fillStyle(gradientColors[i]);
      graphics.fillCircle(size / 2, size / 2, radius);
    }

    try {
      // Generate the texture
      graphics.generateTexture(SharedResources.GRADIENT_TEXTURE_KEY, size, size);
      console.log(`Blue gradient texture created successfully with size ${size}x${size}`);
    } catch (error) {
      console.error('Error creating gradient texture:', error);
      
      // Fallback to an even smaller texture size if there was an error
      if (!isMobile) {
        try {
          console.log('Attempting fallback with smaller texture size');
          graphics.clear();
          const fallbackSize = 1024;
          
          // Redraw at smaller size
          const fallbackSectionSize = fallbackSize / 2 / mobileColorStops.length;
          for (let i = 0; i < mobileColorStops.length; i++) {
            const radius = fallbackSize / 2 - i * fallbackSectionSize;
            graphics.fillStyle(mobileColorStops[i]);
            graphics.fillCircle(fallbackSize / 2, fallbackSize / 2, radius);
          }
          
          graphics.generateTexture(SharedResources.GRADIENT_TEXTURE_KEY, fallbackSize, fallbackSize);
          console.log('Fallback blue gradient texture created successfully');
        } catch (fallbackError) {
          console.error('Fallback texture creation failed:', fallbackError);
          console.error('Your device may not support the texture size or WebGL features required');
        }
      } else {
        console.error('Mobile device detected - not attempting fallback. Error details:', 
                     error instanceof Error ? error.message : String(error));
      }
    } finally {
      // Clean up the graphics object
      graphics.destroy();
    }
  }
  
  /**
   * Create a gradient background sprite at the specified position
   * @param scene The scene to add the sprite to
   * @param x X position (default 0)
   * @param y Y position (default 0)
   * @returns The created sprite
   */
  public createGradientSprite(scene: Phaser.Scene, x: number = 0, y: number = 0): Phaser.GameObjects.Sprite {
    // Make sure the texture exists
    this.createGradientTexture();
    
    // Create the sprite
    const sprite = scene.add.sprite(x, y, SharedResources.GRADIENT_TEXTURE_KEY);
    sprite.setOrigin(0.5);
    
    // Use a more moderate depth value that's still behind everything else
    sprite.setDepth(-100);
    sprite.name = 'worldGradientBackground';
    
    // Scale the sprite for visibility - especially important on mobile
    this.adjustGradientSpriteScale(scene, sprite);
    
    // Log the creation
    console.log('Created gradient sprite:', 
      `position(${sprite.x}, ${sprite.y})`, 
      `scale(${sprite.scaleX}, ${sprite.scaleY})`, 
      `depth(${sprite.depth})`
    );
    
    return sprite;
  }
  
  /**
   * Adjust the sprite scale based on device and game size
   * @param scene The current scene
   * @param sprite The gradient sprite to adjust
   */
  private adjustGradientSpriteScale(scene: Phaser.Scene, sprite: Phaser.GameObjects.Sprite): void {
    // Check if we're on a mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
                    (scene.game.device.os.android || scene.game.device.os.iOS);
    
    // Get game dimensions
    const gameWidth = scene.scale.width;
    const gameHeight = scene.scale.height;
    const largerDimension = Math.max(gameWidth, gameHeight);
    
    // Calculate appropriate scale based on game size
    // For mobile, we use a larger scale to ensure the gradient covers the view
    const baseScale = isMobile ? 3 : 2;
    
    // Apply scale - ensure the gradient is large enough to cover the game area
    sprite.setScale(baseScale);
    
    console.log(`Gradient sprite adjusted for ${isMobile ? 'mobile' : 'desktop'}, scale: ${baseScale}`);
  }
  
  /**
   * Increment the reference count for a resource
   * @param key The resource key
   */
  private incrementRefCount(key: string): void {
    const count = this.refCounts.get(key) || 0;
    this.refCounts.set(key, count + 1);
    console.log(`Incremented ref count for ${key} to ${count + 1}`);
  }
  
  /**
   * Decrement the reference count for a resource and clean up if no more references
   * @param key The resource key
   */
  public releaseResource(key: string): void {
    const count = this.refCounts.get(key) || 0;
    
    if (count <= 1) {
      // Last reference, remove the resource
      this.refCounts.delete(key);
      this.resources.delete(key);
      
      // Remove texture if it exists and we have a scene
      if (key === SharedResources.GRADIENT_TEXTURE_KEY && this.scene?.textures.exists(key)) {
        console.log('Removing gradient texture as all references are gone');
        this.scene.textures.remove(key);
      }
    } else {
      // Decrement reference count
      this.refCounts.set(key, count - 1);
      console.log(`Released ${key}, ref count now:`, count - 1);
    }
  }
  
  /**
   * Release all resources
   */
  public releaseAll(): void {
    if (this.scene) {
      for (const key of this.resources.keys()) {
        if (key === SharedResources.GRADIENT_TEXTURE_KEY && this.scene.textures.exists(key)) {
          console.log('Removing texture:', key);
          this.scene.textures.remove(key);
        }
      }
    }
    
    this.resources.clear();
    this.refCounts.clear();
    console.log('All shared resources released');
  }

  /**
   * Check if the minimal mitochondrion texture exists
   */
  public hasMitochondrionMinimalTexture(): boolean {
    return this.resources.has(SharedResources.MITOCHONDRION_MIN_TEXTURE_KEY) || 
           (this.scene?.textures.exists(SharedResources.MITOCHONDRION_MIN_TEXTURE_KEY) ?? false);
  }

  /**
   * Preload the minimal mitochondrion texture
   * @param scene The scene to use for loading
   */
  public preloadMitochondrionMinimalTexture(scene: Phaser.Scene): void {
    if (!scene.textures.exists(SharedResources.MITOCHONDRION_MIN_TEXTURE_KEY)) {
      console.log('Preloading minimal mitochondrion texture from assets/images/mito_v3_min.png');
      // Load the image directly from the public path
      scene.load.image(SharedResources.MITOCHONDRION_MIN_TEXTURE_KEY, 'assets/images/mito_v3_min.png');
    }
  }

  /**
   * Create a minimal mitochondrion sprite
   * @param scene The scene to add the sprite to
   * @param x X position
   * @param y Y position
   * @returns The created sprite
   */
  public createMinimalMitochondrionSprite(scene: Phaser.Scene, x: number, y: number): Phaser.GameObjects.Sprite {
    // Ensure the texture exists
    if (!this.hasMitochondrionMinimalTexture()) {
      this.preloadMitochondrionMinimalTexture(scene);
      
      // If the load isn't started yet, start it
      if (scene.load.isLoading() === false) {
        scene.load.start();
      }
      
      // Fallback to standard sprite if minimal texture not available
      if (!this.hasMitochondrionMinimalTexture()) {
        console.warn('Minimal mitochondrion texture not available, using standard texture');
        return this.createMitochondrionSprite(scene, x, y);
      }
    }
    
    // Use the minimal outline version
    const sprite = scene.add.sprite(x, y, SharedResources.MITOCHONDRION_MIN_TEXTURE_KEY);
    
    // Make sure transparency is supported
    sprite.setPipeline('MultiPipeline');
    sprite.setBlendMode(Phaser.BlendModes.NORMAL);
    
    // Scale appropriately
    sprite.setScale(0.25); // Adjust scale as needed
    
    console.log('Created minimal mitochondrion sprite:', 
      `position(${sprite.x}, ${sprite.y})`
    );
    
    return sprite;
  }

  /**
   * Check if a specific image is loaded in the textures manager
   * @param key The texture key to check
   * @returns True if the image is loaded, false otherwise
   */
  private hasLoadedImage(key: string): boolean {
    return this.resources.has(key) || (this.scene?.textures.exists(key) ?? false);
  }

  /**
   * Check if the lysosome texture exists
   */
  public hasLysosomeTexture(): boolean {
    return this.resources.has(SharedResources.LYSOSOME_TEXTURE_KEY) || 
           (this.scene?.textures.exists(SharedResources.LYSOSOME_TEXTURE_KEY) ?? false);
  }

  /**
   * Preload the lysosome texture
   * @param scene The scene to use for loading
   */
  public preloadLysosomeTexture(scene: Phaser.Scene): void {
    if (!scene.textures.exists(SharedResources.LYSOSOME_TEXTURE_KEY)) {
      console.log('Preloading lysosome texture from assets/images/lyso_v2_min.png');
      // Load the image directly from the public path
      scene.load.image(SharedResources.LYSOSOME_TEXTURE_KEY, 'assets/images/lyso_v2_min.png');
    }
  }

  /**
   * Create a lysosome sprite
   * @param scene The scene to add the sprite to
   * @param x X position
   * @param y Y position
   * @returns The created sprite
   */
  public createLysosomeSprite(scene: Phaser.Scene, x: number, y: number): Phaser.GameObjects.Sprite {
    // Ensure the texture exists
    if (!this.hasLysosomeTexture()) {
      this.preloadLysosomeTexture(scene);
      
      // If the load isn't started yet, start it
      if (scene.load.isLoading() === false) {
        scene.load.start();
      }
      
      // Create a simple fallback while texture loads
      const fallbackSprite = scene.add.ellipse(x, y, 16, 14, 0xFF8844);
      return scene.add.sprite(x, y, '__WHITE');
    }
    
    // Create the sprite
    const sprite = scene.add.sprite(x, y, SharedResources.LYSOSOME_TEXTURE_KEY);
    sprite.setOrigin(0.5);
    
    // Make sure transparency is supported
    sprite.setPipeline('MultiPipeline');
    sprite.setBlendMode(Phaser.BlendModes.NORMAL);
    
    console.log('Created lysosome sprite:', 
      `position(${sprite.x}, ${sprite.y})`
    );
    
    return sprite;
  }

  /**
   * Check if the glucose texture exists
   */
  public hasGlucoseTexture(): boolean {
    return this.resources.has(SharedResources.GLUCOSE_TEXTURE_KEY) || 
           (this.scene?.textures.exists(SharedResources.GLUCOSE_TEXTURE_KEY) ?? false);
  }

  /**
   * Preload the glucose texture
   * @param scene The scene to use for loading
   */
  public preloadGlucoseTexture(scene: Phaser.Scene): void {
    if (!scene.textures.exists(SharedResources.GLUCOSE_TEXTURE_KEY)) {
      console.log('Preloading glucose texture from assets/images/glucose_amber_v1.png');
      // Load the image directly from the public path
      scene.load.image(SharedResources.GLUCOSE_TEXTURE_KEY, 'assets/images/glucose_amber_v1.png');
    }
  }

  /**
   * Create a glucose sprite
   * @param scene The scene to add the sprite to
   * @param x X position
   * @param y Y position
   * @returns The created sprite
   */
  public createGlucoseSprite(scene: Phaser.Scene, x: number, y: number): Phaser.GameObjects.Sprite {
    // Ensure the texture exists
    if (!this.hasGlucoseTexture()) {
      this.preloadGlucoseTexture(scene);
      
      // If the load isn't started yet, start it
      if (scene.load.isLoading() === false) {
        scene.load.start();
      }
      
      console.log('Glucose texture not yet loaded, creating temporary hex sprite');
      
      // Create a debug text to indicate this is a fallback
      const debugText = scene.add.text(x, y + 20, 'Loading...', {
        fontSize: '10px',
        color: '#FFFFFF'
      }).setOrigin(0.5);
      
      // Create a visible hexagon fallback
      const graphics = scene.add.graphics();
      const radius = 8;
      
      // Draw a hexagon with amber fill
      graphics.fillStyle(0xFFCC88, 1);
      
      // Create hexagon points
      const points = [];
      for (let i = 0; i < 6; i++) {
        const angle = (i * Math.PI / 3) - Math.PI / 2;
        const pointX = x + radius * Math.cos(angle);
        const pointY = y + radius * Math.sin(angle);
        points.push({ x: pointX, y: pointY });
      }
      
      // Draw the hexagon
      graphics.beginPath();
      graphics.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < 6; i++) {
        graphics.lineTo(points[i].x, points[i].y);
      }
      graphics.closePath();
      graphics.fillPath();
      
      // Add white border
      graphics.lineStyle(2, 0xFFFFFF, 0.8);
      graphics.beginPath();
      graphics.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < 6; i++) {
        graphics.lineTo(points[i].x, points[i].y);
      }
      graphics.closePath();
      graphics.strokePath();
      
      // Create the fallback sprite (placeholder)
      const fallbackSprite = scene.add.sprite(x, y, '__WHITE');
      fallbackSprite.setAlpha(0); // Make it invisible, we'll use the graphics instead
      
      // Clean up the graphics when the texture finally loads
      scene.load.on('filecomplete', (key: string) => {
        if (key === SharedResources.GLUCOSE_TEXTURE_KEY) {
          console.log('Glucose texture loaded, replacing fallback');
          graphics.destroy();
          debugText.destroy();
          // We'll let the caller handle replacing this sprite
        }
      });
      
      return fallbackSprite;
    }
    
    // Create the sprite with the loaded texture
    const sprite = scene.add.sprite(x, y, SharedResources.GLUCOSE_TEXTURE_KEY);
    sprite.setOrigin(0.5);
    
    // Set a specific size to exactly 1/3 of the previous 16px
    // Our original image is 42x42, so we calculate the scale precisely
    const targetSize = 5.3; // Approximately 1/3 of previous 16 pixels
    const scale = targetSize / sprite.width;
    sprite.setScale(scale);
    
    // Make sure transparency is supported
    sprite.setPipeline('MultiPipeline');
    sprite.setBlendMode(Phaser.BlendModes.NORMAL);
    
    console.log('Created glucose sprite:', 
      `position(${sprite.x}, ${sprite.y})`,
      `scale(${sprite.scaleX})`,
      `size(${sprite.width * scale}px)`
    );
    
    return sprite;
  }
} 